import * as React from "react";
import { useDispatch } from "react-redux";
import { Input, Select as DeprecatedAntdSelect } from "antd";
import { consultNoteOperations } from "../../../../../state/models/consultnote";
import { substanceOptions, frequencyOptions, intensityOptions } from "./enums";
import { Flex, Row } from "../../../../_layout/Flex";
import { SubstanceUse } from "../../../../../api/types";
import useDebounce from "../../../../hooks/useDebounce";
import { SelectSearchProps } from "../shared/SelectSearchProps";
import { getCurrentConsultNoteInfo } from "@/app/consults-and-matching/_hooks";

/**
 * @prop {number} orderNumber represents the order in which the subquestion appears on
 * the form the backend returns the last updated question first, so we want to maintain
 * the original order in which the questions are presented to the clinician
 * @prop {SubstanceUse | undefined} entry represents an entry for SubstanceUse use which
 * includes severity, details, etc. see SubstanceUse interface for more details.
 */

interface SubstanceUseSubQuestionProps {
  orderNumber: number;
  entry: SubstanceUse | undefined;
  handleEdit: (hasBeenEdited: boolean) => void;
}

const SubstanceUseSubQuestion = ({
  orderNumber,
  entry,
  handleEdit,
}: SubstanceUseSubQuestionProps) => {
  const { readOnlyConsultNote } = getCurrentConsultNoteInfo();

  const [additionalDetails, setAdditionalDetails] = React.useState(
    entry?.additional_details ? entry?.additional_details : "",
  );

  const dispatch = useDispatch();

  const handleSetAdditionalDetails = (value: string) => {
    setAdditionalDetails(value);
    handleEdit(true);
  };

  const handleChange = (
    collection: string,
    id: string | undefined,
    key: string,
    value: string,
    order: number,
  ) => {
    dispatch(
      consultNoteOperations.updateNestedField({
        collection,
        id,
        key,
        value,
        order,
      }),
    );
  };

  const debouncedAdditionalDetails = useDebounce(additionalDetails, 500);

  React.useEffect(() => {
    handleChange(
      "substanceuses",
      entry?.id,
      "additional_details",
      additionalDetails,
      orderNumber,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAdditionalDetails]);

  return (
    <div style={{ marginBottom: "30px" }}>
      <Row>
        <Flex style={{ paddingRight: "20px" }}>
          <div>
            <span style={{ paddingRight: "10px" }}>{orderNumber})</span>{" "}
            Substance
          </div>
          <DeprecatedAntdSelect<string>
            disabled={readOnlyConsultNote}
            value={entry?.substance}
            style={{ width: "100%" }}
            onChange={(v) =>
              handleChange(
                "substanceuses",
                entry?.id,
                "substance",
                v,
                orderNumber,
              )
            }
            {...SelectSearchProps}
          >
            {substanceOptions.map((so) => {
              return (
                <DeprecatedAntdSelect.Option key={so.key} value={so.value}>
                  {so.title}
                </DeprecatedAntdSelect.Option>
              );
            })}
          </DeprecatedAntdSelect>
        </Flex>
        <Flex style={{ paddingRight: "20px" }}>
          <div>Frequency</div>
          <DeprecatedAntdSelect<string>
            disabled={readOnlyConsultNote}
            style={{ width: "100%" }}
            value={entry?.frequency}
            onChange={(v) =>
              handleChange(
                "substanceuses",
                entry?.id,
                "frequency",
                v,
                orderNumber,
              )
            }
            {...SelectSearchProps}
          >
            {frequencyOptions.map((so) => {
              return (
                <DeprecatedAntdSelect.Option key={so.key} value={so.value}>
                  {so.title}
                </DeprecatedAntdSelect.Option>
              );
            })}
          </DeprecatedAntdSelect>
        </Flex>

        <Flex>
          <div>Intensity</div>
          <DeprecatedAntdSelect<string>
            disabled={readOnlyConsultNote}
            style={{ width: "100%" }}
            value={entry?.intensity}
            onChange={(v) =>
              handleChange(
                "substanceuses",
                entry?.id,
                "intensity",
                v,
                orderNumber,
              )
            }
            {...SelectSearchProps}
          >
            {intensityOptions.map((so) => {
              return (
                <DeprecatedAntdSelect.Option key={so.key} value={so.value}>
                  {so.title}
                </DeprecatedAntdSelect.Option>
              );
            })}
          </DeprecatedAntdSelect>
        </Flex>
      </Row>
      <div>Additional details (if needed)</div>
      <Input.TextArea
        disabled={readOnlyConsultNote}
        style={{ height: "32px" }}
        onChange={(e) => handleSetAdditionalDetails(e.target.value)}
        value={additionalDetails}
      />
    </div>
  );
};

export { SubstanceUseSubQuestion };
